import * as DTActions from './diagnosis-treatments.actions';

import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { DiagnosisTreatmentsService } from '@nrwl/rx-service-diagnosis-treatments';
import { DiagnosisTreanment } from '@nrwl/rx-models';

@Injectable()
export class DiagnosisTreatmentsEffects {
  private actions$ = inject(Actions);
  private service = inject(DiagnosisTreatmentsService);

  LoadUsedDiagnosis$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DTActions.ActionsTypes.LoadUsedDiagnosis),
      mergeMap(() =>
        this.service.getUsedDiagnosis().pipe(
          switchMap((res: DiagnosisTreanment[]) => [new DTActions.LoadUsedDiagnosisSuccess(res)]),
          catchError((err: any) => of(new DTActions.Error(err))),
        ),
      ),
    );
  });

  LoadUsedTreatments$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DTActions.LoadUsedTreatments>(DTActions.ActionsTypes.LoadUsedTreatments),
      mergeMap(() =>
        this.service.getUsedTreatments().pipe(
          switchMap((res: DiagnosisTreanment[]) => [new DTActions.LoadUsedTreatmentsSuccess(res)]),
          catchError((err: any) => of(new DTActions.Error(err))),
        ),
      ),
    ),
  );

  LoadUsedSurgeons$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DTActions.LoadUsedSurgeons>(DTActions.ActionsTypes.LoadUsedSurgeons),
      mergeMap(() =>
        this.service.getUsedSurgeons().pipe(
          switchMap((res: { results: DiagnosisTreanment[] }) => [
            new DTActions.LoadUsedSurgeonsSuccess(res),
          ]),
          catchError((err: any) => of(new DTActions.Error(err))),
        ),
      ),
    ),
  );

  LoadUsedBodyParts$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DTActions.LoadUsedBodyParts>(DTActions.ActionsTypes.LoadUsedBodyParts),
      mergeMap(() =>
        this.service.getUsedBodyParts().pipe(
          switchMap((res: { results: any[] }) => [new DTActions.LoadUsedBodyPartsSuccess(res)]),
          catchError((err: any) => of(new DTActions.Error(err))),
        ),
      ),
    ),
  );

  LoadUsedVisitTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DTActions.LoadUsedVisitTypes>(DTActions.ActionsTypes.LoadUsedVisitTypes),
      mergeMap(() =>
        this.service.getUsedVisitTypes().pipe(
          switchMap((res: { results: DiagnosisTreanment[] }) => [
            new DTActions.LoadUsedVisitTypesSuccess(res),
          ]),
          catchError((err: any) => of(new DTActions.Error(err))),
        ),
      ),
    ),
  );

  LoadUsedProcedureTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DTActions.LoadUsedProcedureTypes>(DTActions.ActionsTypes.LoadUsedProcedureTypes),
      mergeMap(() =>
        this.service.getUsedProcedureTypes().pipe(
          switchMap((res: { results: DiagnosisTreanment[] }) => [
            new DTActions.LoadUsedProcedureTypesSuccess(res),
          ]),
          catchError((err: any) => of(new DTActions.Error(err))),
        ),
      ),
    ),
  );

  LoadFormTitles$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DTActions.LoadFormTitles>(DTActions.ActionsTypes.LoadFormTitles),
      mergeMap(() =>
        this.service.getFormTitlesForFilter().pipe(
          switchMap((res: { results: any[] }) => [new DTActions.LoadFormTitlesSuccess(res)]),
          catchError((err: any) => of(new DTActions.Error(err))),
        ),
      ),
    ),
  );

  LoadFormProviders$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DTActions.LoadFormProviders>(DTActions.ActionsTypes.LoadFormProviders),
      mergeMap(() =>
        this.service.getFormProvidersForFilter().pipe(
          switchMap((res: { results: any[] }) => [new DTActions.LoadFormProvidersSuccess(res)]),
          catchError((err: any) => of(new DTActions.Error(err))),
        ),
      ),
    ),
  );

  Search$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DTActions.Search>(DTActions.ActionsTypes.Search),
      mergeMap(() =>
        this.service.getUsedTreatments().pipe(
          map((res: DiagnosisTreanment[]) => new DTActions.LoadUsedTreatmentsSuccess(res)),
          catchError((err: any) => of(new DTActions.Error(err))),
        ),
      ),
    ),
  );

  LoadAreaImplants$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DTActions.LoadAreaImplants>(DTActions.ActionsTypes.LoadAreaImplants),
      mergeMap(() =>
        this.service.getAreaImplants().pipe(
          switchMap((res: any) => [new DTActions.LoadAreaImplantsSuccess(res)]),
          catchError((err: any) => of(new DTActions.Error(err))),
        ),
      ),
    ),
  );

  LoadImplantVolumes$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DTActions.LoadImplantVolumes>(DTActions.ActionsTypes.LoadImplantVolumes),
      mergeMap(() =>
        this.service.getImplantVolumes().pipe(
          switchMap((res: any) => [new DTActions.LoadImplantVolumesSuccess(res)]),
          catchError((err: any) => of(new DTActions.Error(err))),
        ),
      ),
    ),
  );

  LoadImplantPlanes$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DTActions.LoadImplantPlanes>(DTActions.ActionsTypes.LoadImplantPlanes),
      mergeMap(() =>
        this.service.getImplantPlanes().pipe(
          switchMap((res: any) => [new DTActions.LoadImplantPlanesSuccess(res)]),
          catchError((err: any) => of(new DTActions.Error(err))),
        ),
      ),
    ),
  );

  LoadBMIData$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DTActions.LoadBMIData>(DTActions.ActionsTypes.LoadBMIData),
      mergeMap(() =>
        this.service.getBMIData().pipe(
          switchMap((res: any) => [new DTActions.LoadBMIDataSuccess(res)]),
          catchError((err: any) => of(new DTActions.Error(err))),
        ),
      ),
    ),
  );

  LoadSkinToneData$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DTActions.LoadSkinToneData>(DTActions.ActionsTypes.LoadSkinToneData),
      mergeMap(() =>
        this.service.getSkinToneData().pipe(
          switchMap((res: any) => [new DTActions.LoadSkinToneDataSuccess(res)]),
          catchError((err: any) => of(new DTActions.Error(err))),
        ),
      ),
    ),
  );
}
